import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Modal, Form, Input, Select, InputNumber, Switch, Button, Row, Col, Checkbox, DatePicker, message } from 'antd';
import moment from 'moment';
import { educationAssetService } from '@iso/services';
import AssessmentFormSelect from './AssessmentFormSelect';

const { Option } = Select;
const { TextArea } = Input;

const CATEGORIES = [
  { value: 'induction', label: 'Induction' },
  { value: 'safety', label: 'Safety' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'technical', label: 'Technical' },
  { value: 'whs', label: 'WHS' },
  { value: 'other', label: 'Other' },
];

const AssetModal = ({ visible, asset, propertyId, userList = [], onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [noExpiry, setNoExpiry] = useState(false);
  const [primaryIsExternal, setPrimaryIsExternal] = useState(false);
  const [secondaryHasExternal, setSecondaryHasExternal] = useState(false);
  const currentUser = useSelector((state) => state.user.user);
  const currentUserId = currentUser ? currentUser.id : null;

  useEffect(() => {
    if (visible) {
      if (asset) {
        // "No expiry" is represented in the DB as a NULL validity_months.
        const noExp = asset.validity_months === null || asset.validity_months === undefined;
        setNoExpiry(noExp);
        const ext = asset.primary_external_trainer || null;
        setPrimaryIsExternal(!!ext);
        // The column stores a JSON array but the UI exposes a single external
        // secondary slot, matching the Assets-module modal. Read the first
        // entry only; re-saving here replaces the rest (documented in
        // EducationAssetService::createOrUpdate).
        const secExtList = asset.secondary_external_trainers || [];
        const secExt = secExtList.length > 0 ? secExtList[0] : null;
        setSecondaryHasExternal(!!secExt);
        form.setFieldsValue({
          name: asset.name,
          type: asset.type,
          category: asset.category,
          description: asset.description,
          validity_months: noExp ? null : asset.validity_months,
          primary_trainer_id: ext ? 'external' : asset.primary_trainer_id,
          primary_ext_name: ext ? ext.name : undefined,
          primary_ext_email: ext ? ext.email : undefined,
          primary_ext_company: ext ? ext.company : undefined,
          primary_ext_phone: ext ? ext.phone : undefined,
          secondary_trainer_ids: [
            ...(asset.secondary_trainer_ids || []),
            ...(secExt ? ['external'] : []),
          ],
          sec_ext_name: secExt ? secExt.name : undefined,
          sec_ext_email: secExt ? secExt.email : undefined,
          sec_ext_company: secExt ? secExt.company : undefined,
          sec_ext_phone: secExt ? secExt.phone : undefined,
          active_date: asset.active_date ? moment(asset.active_date) : null,
          expiry_date: asset.expiry_date ? moment(asset.expiry_date) : null,
          available_to_contractors: !!asset.available_to_contractors,
          assessment_form_id: asset.assessment_form_id || null,
          status: asset.status || 'active',
        });
      } else {
        setNoExpiry(false);
        setPrimaryIsExternal(false);
        setSecondaryHasExternal(false);
        form.resetFields();
        form.setFieldsValue({
          validity_months: 12,
          // Matches the Assets-module modal, which opens a new asset active
          // from today. The backend defaults this too, but setting it here
          // means the user can see and change it before saving.
          active_date: moment(),
          secondary_trainer_ids: [],
          status: 'active',
          available_to_contractors: false,
          assessment_form_id: null,
          // Default the creator as the primary trainer so they can see and
          // assign the asset immediately afterwards (Health & Safety filters
          // on trainer membership). User can clear or change before save.
          primary_trainer_id: currentUserId || undefined,
        });
      }
    }
  }, [visible, asset, currentUserId]);

  const onToggleNoExpiry = useCallback((e) => {
    const checked = e.target.checked;
    setNoExpiry(checked);
    // When toggling on, clear the InputNumber so the form value matches what
    // we'll send (null). When toggling off, restore a sensible default.
    // expiry_date goes with it: a one-off training that never expires must not
    // keep a stale date the model would otherwise carry through to the record.
    form.setFieldsValue({
      validity_months: checked ? null : 12,
      ...(checked ? { expiry_date: null } : {}),
    });
  }, [form]);

  const handleSubmit = async () => {
    // Set saving immediately on click — validateFields() is async and without
    // this guard a fast double-click can launch two concurrent createOrUpdate
    // calls before the first response returns.
    setSaving(true);
    try {
      const values = await form.validateFields();
      const isExternal = values.primary_trainer_id === 'external';
      // 'external' is a sentinel option in the same multi-select as the real
      // user ids — split it back out before sending, or the backend would try
      // to store the string as a trainer id.
      const secIds = (values.secondary_trainer_ids || []).filter(id => id !== 'external');
      const hasSecExt = (values.secondary_trainer_ids || []).includes('external');
      const data = {
        ...values,
        property_id: propertyId,
        active_date: values.active_date ? values.active_date.format('YYYY-MM-DD') : null,
        // Null when "No expiry" is checked, for the same reason validity_months
        // is: the field is disabled but may still hold a previous value.
        expiry_date: (!noExpiry && values.expiry_date) ? values.expiry_date.format('YYYY-MM-DD') : null,
        secondary_trainer_ids: secIds,
        secondary_external_trainers: hasSecExt ? [{
          name: values.sec_ext_name,
          email: values.sec_ext_email,
          company: values.sec_ext_company || null,
          phone: values.sec_ext_phone,
        }] : [],
        available_to_contractors: values.available_to_contractors ? 1 : 0,
        // Force null on submit when "No expiry" is checked, regardless of
        // whatever last value the InputNumber may have held.
        validity_months: noExpiry ? null : values.validity_months,
        // An explicit null clears the default assessment — the backend has to
        // see the key to know the difference between "clear it" and "leave it".
        assessment_form_id: values.assessment_form_id || null,
        primary_trainer_id: isExternal ? null : values.primary_trainer_id,
        primary_external_trainer: isExternal ? {
          name: values.primary_ext_name,
          email: values.primary_ext_email,
          company: values.primary_ext_company || null,
          phone: values.primary_ext_phone,
        } : null,
      };
      delete data.primary_ext_name;
      delete data.primary_ext_email;
      delete data.primary_ext_company;
      delete data.primary_ext_phone;
      delete data.sec_ext_name;
      delete data.sec_ext_email;
      delete data.sec_ext_company;
      delete data.sec_ext_phone;
      const res = await educationAssetService.storeOrUpdate(data, asset ? asset.id : null);
      if (res && res.code === '0000') {
        onSaved && onSaved(res.education_asset);
      } else {
        // Non-success envelope (e.g. {code:'1001', message:'...'}). Surface
        // the backend-provided message when available so a validation failure
        // returned as 200 doesn't disappear silently.
        message.error((res && res.message) || 'Failed to save training asset.');
      }
    } catch (e) {
      // AntD form validation rejections carry errorFields and are already
      // rendered inline on the form — don't toast over them.
      if (e && e.errorFields) return;
      console.error('Failed to save training asset:', e);
      message.error('Failed to save training asset. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={asset ? 'Edit Training Asset' : 'Add Training Asset'}
      open={visible}
      onCancel={onClose}
      width={600}
      footer={[
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        <Button
          key="save"
          type="primary"
          loading={saving}
          onClick={handleSubmit}
          style={{ background: '#0B2A1B', borderColor: '#0B2A1B' }}
        >
          {asset ? 'Save Changes' : 'Add Asset'}
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        <Form.Item name="name" label="Asset Name" rules={[{ required: true, whitespace: true, message: 'Name is required' }]}>
          <Input placeholder="e.g. Site Induction" />
        </Form.Item>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="type" label="Education Type" rules={[{ required: true, message: 'Type is required' }]}>
              <Select placeholder="Select type">
                <Option value="course">Course</Option>
                <Option value="training">Training</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="category" label="Category">
              <Select placeholder="Select category">
                {CATEGORIES.map(c => (
                  <Option key={c.value} value={c.value}>{c.label}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="validity_months" label="Validity Period (months)">
              <InputNumber min={1} max={120} style={{ width: '100%' }} disabled={noExpiry} />
            </Form.Item>
            <Checkbox
              checked={noExpiry}
              onChange={onToggleNoExpiry}
              style={{ marginTop: -4, marginBottom: 12 }}
            >
              No expiry (one-off training)
            </Checkbox>
          </Col>
          <Col span={12}>
            <Form.Item name="status" label="Status">
              <Select>
                <Option value="active">Active</Option>
                <Option value="inactive">Inactive</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="primary_trainer_id" label="Primary Trainer">
          <Select
            placeholder="Select trainer"
            allowClear
            showSearch
            optionFilterProp="children"
            onChange={(val) => setPrimaryIsExternal(val === 'external')}
          >
            <Option key="external" value="external" style={{ fontStyle: 'italic' }}>External Trainer</Option>
            {userList.map(u => (
              <Option key={u.id} value={u.id}>{u.name}</Option>
            ))}
          </Select>
        </Form.Item>

        {primaryIsExternal && (
          <div style={{ background: '#f9f9f9', border: '1px solid #e8e8e8', borderRadius: 4, padding: '12px 16px', marginBottom: 16 }}>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="primary_ext_name" label="Full Name" rules={[{ required: true, whitespace: true, message: 'Full name is required' }]}>
                  <Input placeholder="Trainer full name" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="primary_ext_email" label="Email" rules={[{ required: true, whitespace: true, type: 'email', message: 'Valid email is required' }]}>
                  <Input placeholder="trainer@example.com" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="primary_ext_phone" label="Phone Number" rules={[{ required: true, whitespace: true, message: 'Phone number is required' }]}>
                  <Input placeholder="+61 400 000 000" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="primary_ext_company" label="Company Name">
                  <Input placeholder="Company (optional)" />
                </Form.Item>
              </Col>
            </Row>
          </div>
        )}

        <Form.Item name="secondary_trainer_ids" label="Secondary Trainers">
          <Select
            mode="multiple"
            placeholder="Select secondary trainers"
            showSearch
            optionFilterProp="children"
            onChange={(vals) => setSecondaryHasExternal((vals || []).includes('external'))}
          >
            <Option key="external" value="external" style={{ fontStyle: 'italic' }}>External Trainer</Option>
            {userList.map(u => (
              <Option key={u.id} value={u.id}>{u.name}</Option>
            ))}
          </Select>
        </Form.Item>

        {secondaryHasExternal && (
          <div style={{ background: '#f9f9f9', border: '1px solid #e8e8e8', borderRadius: 4, padding: '12px 16px', marginBottom: 16 }}>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="sec_ext_name" label="Full Name" rules={[{ required: true, whitespace: true, message: 'Full name is required' }]}>
                  <Input placeholder="Trainer full name" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="sec_ext_email" label="Email" rules={[{ required: true, whitespace: true, type: 'email', message: 'Valid email is required' }]}>
                  <Input placeholder="trainer@example.com" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="sec_ext_phone" label="Phone Number" rules={[{ required: true, whitespace: true, message: 'Phone number is required' }]}>
                  <Input placeholder="+61 400 000 000" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="sec_ext_company" label="Company Name">
                  <Input placeholder="Company (optional)" />
                </Form.Item>
              </Col>
            </Row>
          </div>
        )}

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="active_date" label="Active Date">
              <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="expiry_date"
              label="Expiry Date"
              extra={noExpiry ? undefined : 'Leave empty to calculate it from the validity period.'}
            >
              <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} disabled={noExpiry} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="assessment_form_id"
          label="Assessment form"
          extra="Attendees who scan this training's QR code are asked to complete this form. Every session of this asset uses it unless the session overrides it."
        >
          <AssessmentFormSelect propertyId={propertyId} placeholder="No assessment" />
        </Form.Item>

        <Form.Item name="description" label="Description">
          <TextArea rows={3} placeholder="Describe the purpose and content of this training asset" />
        </Form.Item>

        <Form.Item name="available_to_contractors" label="Available to Contractors" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AssetModal;
